"use client";

import type {
  AltitudeDay,
  AltitudeStats,
  AltitudeUnit,
} from "@tripcart/shared";

import { cx } from "../../cx";
import { ToggleGroup, ToggleGroupItem } from "../toggle-group";
import styles from "./altitude-chart.module.css";

interface TipLabels {
  startOfTour: string;
  noChange: string;
}

/** The hover / keyboard readout for one day (spec §5.5). */
export function DayReadout({
  index,
  day,
  move,
  unit,
  fmt,
  labels,
  xPct,
}: {
  index: number;
  day: AltitudeDay | undefined;
  move: { up: number; down: number } | undefined;
  unit: AltitudeUnit;
  fmt: (m: number) => string;
  labels: TipLabels;
  xPct: number;
}) {
  return (
    <div
      className={cx(styles.tip, day && styles.tipOn)}
      role="status"
      aria-live="polite"
      data-side={xPct > 60 ? "right" : "left"}
      style={{ "--alt-tip-x": `${xPct}%` } as React.CSSProperties}
    >
      {day && move ? (
        <>
          <b>{day.day}</b>
          {day.stops.map((s, i) => (
            <div key={i} className={styles.tipRow}>
              <span>{s.place}</span>
              <span>
                {fmt(s.alt)} {unit}
              </span>
            </div>
          ))}
          <div className={styles.tipMove}>
            {index === 0 && day.stops.length === 1 ? (
              labels.startOfTour
            ) : move.up === 0 && move.down === 0 ? (
              labels.noChange
            ) : (
              <>
                <span className={styles.up}>
                  ▲ {fmt(move.up)} {unit}
                </span>{" "}
                <span className={styles.down}>
                  ▼ {fmt(move.down)} {unit}
                </span>
              </>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}

interface StatLabels {
  highestPoint: string;
  totalAscent: string;
  totalDescent: string;
}

/** The three summary tiles (spec §5.4). */
export function SummaryTiles({
  stats,
  unit,
  fmt,
  labels,
}: {
  stats: AltitudeStats;
  unit: AltitudeUnit;
  fmt: (m: number) => string;
  labels: StatLabels;
}) {
  const tiles: [string, number, string][] = [
    [labels.highestPoint, stats.highest.alt, `${unit} · ${stats.highest.day}`],
    [labels.totalAscent, stats.totalUp, unit],
    [labels.totalDescent, stats.totalDown, unit],
  ];
  return (
    <div className={styles.stats}>
      {tiles.map(([label, value, suffix]) => (
        <div key={label} className={styles.stat}>
          <div className={styles.statLabel}>{label}</div>
          <div className={styles.statValue}>
            {fmt(value)}
            <small>{suffix}</small>
          </div>
        </div>
      ))}
    </div>
  );
}

/** The traveler's m / ft switch, small, in the chart box's top-right corner.
 *  It sat beside the heading and squeezed it: on a phone the title wrapped to
 *  three lines and the description ran in a narrow column (QA TC-18 #10, #13). */
export function UnitSwitch({
  unit,
  unitLabel,
  onUnitChange,
}: {
  unit: AltitudeUnit;
  unitLabel: string;
  onUnitChange: (unit: AltitudeUnit) => void;
}) {
  return (
    <div className={styles.unit}>
      <ToggleGroup
        value={unit}
        onValueChange={onUnitChange}
        aria-label={unitLabel}
        className={styles.unitGroup}
      >
        <ToggleGroupItem value="m" className={styles.unitItem}>
          m
        </ToggleGroupItem>
        <ToggleGroupItem value="ft" className={styles.unitItem}>
          ft
        </ToggleGroupItem>
      </ToggleGroup>
    </div>
  );
}
